import React, { Fragment, useState, useEffect } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Footer from "../components/Footer";
import { showHttpError, showToast, currentPageAccess } from "../utils/library";
import { SITENAMEALIAS, WEBSERVICE } from "../utils/init";
import { getAuthToken, getAuthUuid } from "../utils/service";

// ─────────────────────────────────────────────────────────────────
// API config
// ─────────────────────────────────────────────────────────────────
const BASE_URL = WEBSERVICE;
const getMerchantId = () => localStorage.getItem(SITENAMEALIAS + "_merchant_id") || "";
const getToken = () => getAuthToken() || "";

const authHeaders = () => ({
    "Content-Type": "application/json",
    "Authorization": `Bearer ${getToken()}`,
    "UUID": getAuthUuid() || "",
});

// GET API - Fetch current processing fee status
const fetchProcessingFeeStatus = async (merchantId) => {
    const response = await fetch(`${BASE_URL}/processing-fee/status/${merchantId}`, {
        headers: authHeaders(),
    });
    if (!response.ok) throw { response: { status: response.status } };
    return response.json();
};

// POST API - Update processing fee status
const updateProcessingFeeStatus = async (merchantId, status) => {
    const response = await fetch(`${BASE_URL}/processing-fee/create`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ merchantId, status }),
    });
    if (!response.ok) throw { response: { status: response.status } };
    return response.json();
};

// ─────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────
const S = {
    pageWrapper: { padding: "36px 24px 60px" },
    headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "28px", flexWrap: "wrap", gap: "16px" },
    pageTitle: { fontSize: "24px", fontWeight: "700", color: "var(--text-color)", margin: 0 },
    pageSubtitle: { fontSize: "13px", opacity: 0.5, margin: "4px 0 0" },
    card: { background: "var(--body-color-less)", borderRadius: "16px", border: "1px solid var(--body-color-less2)", boxShadow: "0 2px 12px rgba(0,0,0,0.06)", marginBottom: "24px", padding: "24px" },
    cardHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" },
    cardHeaderLeft: { display: "flex", alignItems: "center", gap: "14px" },
    iconBox: { width: "42px", height: "42px", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0, background: "rgba(99,91,255,0.1)", color: "#635bff" },
    sectionTitle: { fontSize: "16px", fontWeight: "700", color: "var(--text-color)", margin: 0 },
    sectionDesc: { fontSize: "12px", opacity: 0.5, margin: "2px 0 0" },
    formGroup: { display: "flex", flexDirection: "column", gap: "6px" },
    label: { fontSize: "12px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.3px", color: "var(--text-color)", opacity: 0.7 },
    btnPrimary: { padding: "10px 24px", background: "var(--btn-color)", color: "#fff", border: "none", borderRadius: "10px", fontSize: "13px", fontWeight: "600", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "6px" },
    infoBox: { display: "flex", alignItems: "center", gap: "12px", padding: "16px 20px", borderRadius: "10px", background: "linear-gradient(135deg, rgba(99,91,255,0.06), rgba(99,91,255,0.02))", border: "1px solid rgba(99,91,255,0.15)", marginBottom: "24px" },
};

// ─────────────────────────────────────────────────────────────────
// UI helpers
// ─────────────────────────────────────────────────────────────────
function RadioDot({ active }) {
    return (
        <div style={{ width: "16px", height: "16px", borderRadius: "50%", border: active ? "5px solid var(--btn-color)" : "2px solid rgba(128,128,128,0.4)", background: "var(--body-color)", flexShrink: 0, marginTop: "1px", transition: "all 0.15s" }} />
    );
}

function ProcessingFeeField({ value, onChange }) {
    const optStyle = (val) => ({
        flex: 1, display: "flex", alignItems: "flex-start", gap: "10px", padding: "12px 14px",
        borderRadius: "10px",
        border: value === val ? "1.5px solid var(--btn-color)" : "1.5px solid var(--body-color-less2)",
        background: value === val ? "color-mix(in srgb, var(--btn-color), transparent 93%)" : "var(--body-color)",
        cursor: "pointer", transition: "all 0.15s",
    });
    return (
        <div style={S.formGroup}>
            <label style={S.label}>Processing Fee Handling</label>
            <div style={{ display: "flex", gap: "10px" }}>
                <div style={optStyle("customer")} onClick={() => onChange("customer")}>
                    <RadioDot active={value === "customer"} />
                    <div>
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-color)", lineHeight: "1.3" }}>Customer Pays Fee</div>
                        <div style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.5, marginTop: "2px" }}>Fee added on top of checkout total</div>
                    </div>
                </div>
                <div style={optStyle("merchant")} onClick={() => onChange("merchant")}>
                    <RadioDot active={value === "merchant"} />
                    <div>
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--text-color)", lineHeight: "1.3" }}>Merchant Absorbs Fee</div>
                        <div style={{ fontSize: "11px", color: "var(--text-color)", opacity: 0.5, marginTop: "2px" }}>Fee deducted from your payout</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────
export default function FeesHandling() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [processingFeeHandling, setProcessingFeeHandling] = useState("customer");
    const isReadOnly = currentPageAccess() === "READ";

    useEffect(() => { 
        loadProcessingFeeStatus(); 
    }, []);

    const loadProcessingFeeStatus = async () => {
        try {
            setLoading(true);
            const merchantId = getMerchantId();
            const response = await fetchProcessingFeeStatus(merchantId);
            
            // Check if API call was successful
            if (response.status === true && response.data) {
                // Convert API status (1 or 0) to UI value ("customer" or "merchant")
                const status = response.data.isProcessingFeeEnabled;
                setProcessingFeeHandling(status === 1 ? "customer" : "merchant");
            } else {
                // Handle error case - default to customer
                console.error("Failed to fetch processing fee status");
                setProcessingFeeHandling("customer");
            }
        } catch (err) {
            console.error("Error fetching processing fee status:", err);
            showHttpError(err);
            // Set default value on error
            setProcessingFeeHandling("customer");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (isReadOnly) {
            showToast("error", "You need write access to perform this operation");
            return;
        }
        try {
            setSaving(true);
            const merchantId = getMerchantId();
            // Convert UI value ("customer" or "merchant") to API status (1 or 0)
            const status = processingFeeHandling === "customer" ? 1 : 0;
            
            const response = await updateProcessingFeeStatus(merchantId, status);
            
            // Check response
            if (response.status === true) {
                showToast("success", response.data || "Processing fee preference saved successfully.");
            } else {
                showToast("error", "Failed to update processing fee status.");
            }
        } catch (err) {
            console.error("Error saving processing fee status:", err);
            showHttpError(err);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Fragment>
            <div className="container-scroller">
                <Sidebar />
                <Header />
                <div className="main-panel">
                    <div style={S.pageWrapper}>
                        <div style={S.headerRow}>
                            <div>
                                <h1 style={S.pageTitle}>Fees Handling</h1>
                                <p style={S.pageSubtitle}>Configure how processing fees are handled for your transactions</p>
                            </div>
                        </div>

                        <div style={S.card}>
                            <div style={S.cardHeader}>
                                <div style={S.cardHeaderLeft}>
                                    <div style={S.iconBox}>
                                        <i className="mdi mdi-cash-multiple" />
                                    </div>
                                    <div>
                                        <h3 style={S.sectionTitle}>Processing Fee Settings</h3>
                                        <p style={S.sectionDesc}>Choose who pays the processing fee on each transaction</p>
                                    </div>
                                </div>
                            </div>

                            <div style={S.infoBox}>
                                <i className="mdi mdi-information-outline" style={{ fontSize: "20px", color: "#635bff", flexShrink: 0 }} />
                                <div style={{ fontSize: "12px", color: "var(--text-color)", opacity: 0.7, lineHeight: "1.6" }}>
                                    This setting determines whether the processing fee is added on top of the checkout total (customer pays) or deducted from your payout (merchant absorbs). This applies across all billing profiles.
                                </div>
                            </div>

                            {loading ? (
                                <div style={{ textAlign: "center", padding: "40px 0", color: "var(--text-color)", opacity: 0.5 }}>
                                    <i className="mdi mdi-loading mdi-spin" style={{ fontSize: "24px" }} />
                                    <div style={{ marginTop: "10px", fontSize: "13px" }}>Loading...</div>
                                </div>
                            ) : (
                                <>
                                    <ProcessingFeeField value={processingFeeHandling} onChange={isReadOnly ? () => {} : setProcessingFeeHandling} />

                                    {!isReadOnly && (
                                        <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end" }}>
                                            <button style={S.btnPrimary} onClick={handleSave} disabled={saving}>
                                                {saving ? <><i className="mdi mdi-loading mdi-spin" /> Saving...</> : <><i className="mdi mdi-content-save-outline" /> Save Changes</>}
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>
        </Fragment>
    );
}